/* eslint-disable @typescript-eslint/no-explicit-any, react-hooks/exhaustive-deps */
import React, { useState, useEffect, ReactNode } from "react";
import { X } from "lucide-react";
import { calculatePrice } from "@/utils/product/detail";

interface CustomizationModalProps {
  onClose: () => void;
  onBack?: () => void;
  product: any;
  formData: any;
  onFormDataChange: (newFormData: any) => void;
  onAddToCart: (addedData: any) => void;
  renderHeaderContent?: () => ReactNode;
  isBuyNow?: boolean;
  availableWeightVariantsCount?: number;
}

const CustomizationModal: React.FC<CustomizationModalProps> = ({
  onClose,
  product,
  formData,
  onFormDataChange,
  onAddToCart,
  renderHeaderContent,
  isBuyNow = false,
}) => {
  const [localFormData, setLocalFormData] = useState<any>(formData);
  const [quantity, setQuantity] = useState(1);
  const [validationErrors, setValidationErrors] = useState<{
    [key: string]: string;
  }>({});
  const [interactionErrors, setInteractionErrors] = useState<{
    [key: string]: string;
  }>({});

  useEffect(() => {
    setLocalFormData(formData);
  }, [formData]);

  useEffect(() => {
    setQuantity(formData.count || 1);
  }, [formData.count]);

  // Auto-select default options when modal opens
  useEffect(() => {
    if (product?.customization_info) {
      const updatedFormData = { ...localFormData };
      let hasChanges = false;

      product.customization_info.forEach((section: any) => {
        section.customization_options.forEach((option: any) => {
          if (option.metadata?.is_default && option.is_active) {
            const isSelected = updatedFormData.customizations.some(
              (c: any) => c.id === option.id,
            );
            if (!isSelected) {
              updatedFormData.customizations.push({
                ...option,
                label: section.label,
              });
              hasChanges = true;
            }
          }
        });
      });

      if (hasChanges) {
        setLocalFormData(updatedFormData);
      }
    }
  }, [product?.customization_info]);

  // Validate selections for each section
  const validateSelections = (section: any, selectedOptions: any[]) => {
    const sectionOptions = section.customization_options.filter(
      (opt: any) => opt.is_active,
    );
    const selectedInSection = selectedOptions.filter((opt: any) =>
      sectionOptions.some((sectionOpt: any) => sectionOpt.id === opt.id),
    );

    const _minSelect = section.metadata?.min_select || 0;
    const maxSelect = section.metadata?.max_select || sectionOptions.length;

    // For mandatory selections, always validate minimum
    if (
      section.selection_type === "Mandatory" &&
      selectedInSection.length < _minSelect
    ) {
      return `Please select at least ${_minSelect} option${_minSelect > 1 ? "s" : ""}`;
    }

    // For optional selections, only validate if user has started selecting
    if (
      section.selection_type === "Optional" &&
      selectedInSection.length > 0 &&
      selectedInSection.length < _minSelect
    ) {
      return `Please select at least ${_minSelect} option${_minSelect > 1 ? "s" : ""}`;
    }

    if (selectedInSection.length > maxSelect) {
      return `You can select up to ${maxSelect} option${maxSelect > 1 ? "s" : ""}`;
    }

    return null;
  };

  // Get validation errors for all sections
  const getValidationErrors = () => {
    const errors: { [key: string]: string } = {};

    if (product?.customization_info) {
      product.customization_info.forEach((section: any) => {
        const error = validateSelections(
          section,
          localFormData.customizations || [],
        );
        if (error) {
          errors[section.label] = error;
        }
      });
    }

    return errors;
  };

  // Update validation errors when selections change
  useEffect(() => {
    const errors = getValidationErrors();
    setValidationErrors(errors);
  }, [localFormData.customizations, product?.customization_info]);

  const handleCustomization = (option: any, section: any) => {
    setLocalFormData((prev: any) => {
      let updatedCustomizations = [...prev.customizations];

      const _minSelect = section.metadata?.min_select || 0;
      const maxSelect =
        section.metadata?.max_select ||
        section.customization_options.filter((opt: any) => opt.is_active)
          .length;
      const isRadio = maxSelect === 1;

      if (isRadio) {
        // For radio buttons, remove all options from this section
        const isSelected = updatedCustomizations.some(
          (c: any) => c.id === option.id,
        );
        const sectionOptions = section.customization_options.map(
          (opt: any) => opt.id,
        );
        updatedCustomizations = updatedCustomizations.filter(
          (c: any) => !sectionOptions.includes(c.id),
        );

        // If it was already selected, we leave it unselected (acting like a toggle).
        // If it wasn't selected, we select it.
        if (!isSelected) {
          updatedCustomizations.push({
            ...option,
            label: section.label,
          });
        }
      } else {
        // For checkboxes, toggle the selection
        const isSelected = updatedCustomizations.some(
          (c: any) => c.id === option.id,
        );

        if (isSelected) {
          updatedCustomizations = updatedCustomizations.filter(
            (c: any) => c.id !== option.id,
          );
          setInteractionErrors((prev) => {
            const next = { ...prev };
            delete next[section.label];
            return next;
          });
        } else {
          // Check if adding this option would exceed max_select
          const sectionOptions = section.customization_options.filter(
            (opt: any) => opt.is_active,
          );
          const selectedInSection = updatedCustomizations.filter((opt: any) =>
            sectionOptions.some((sectionOpt: any) => sectionOpt.id === opt.id),
          );

          if (selectedInSection.length < maxSelect) {
            updatedCustomizations.push({
              ...option,
              label: section.label,
            });
            setInteractionErrors((prev) => {
              const next = { ...prev };
              delete next[section.label];
              return next;
            });
          } else {
            setInteractionErrors((prev) => ({
              ...prev,
              [section.label]: `Select up to ${maxSelect} option${maxSelect > 1 ? "s" : ""}. Deselect some options to select new.`,
            }));
          }
        }
      }

      return {
        ...prev,
        customizations: updatedCustomizations,
      };
    });
  };

  const handleQuantityChange = (newQuantity: number) => {
    setQuantity(newQuantity);
    setLocalFormData((prev: any) => ({ ...prev, count: newQuantity }));
  };

  const handleConfirm = () => {
    // Validate weight selection
    if (
      product?.weight_variants?.length > 1 &&
      !localFormData.selectedVariant
    ) {
      setValidationErrors((prev) => ({
        ...prev,
        weight: "Please select a weight variant",
      }));
      return;
    }

    const errors = getValidationErrors();
    if (Object.keys(errors).length > 0) {
      return; // Don't proceed if there are validation errors
    }
    onFormDataChange(localFormData);
    onAddToCart(localFormData);
    onClose();
  };

  const currentPrice = calculatePrice(localFormData, product);

  // Helper function to get appropriate subtext for each section
  const getSectionSubtext = (section: any) => {
    const _minSelect = section.metadata?.min_select || 0;
    const maxSelect =
      section.metadata?.max_select ||
      section.customization_options.filter((opt: any) => opt.is_active).length;

    if (section.selection_type === "Mandatory") {
      if (maxSelect === 1) {
        return "Please select one option";
      } else if (_minSelect === maxSelect) {
        return `Please select exactly ${_minSelect} option${_minSelect > 1 ? "s" : ""}`;
      } else {
        return `Please select ${_minSelect} to ${maxSelect} option${maxSelect > 1 ? "s" : ""}`;
      }
    } else {
      if (_minSelect === 0 && maxSelect === 1) {
        return "Select up to 1 option";
      } else if (_minSelect === 0) {
        return `Select up to ${maxSelect} option${maxSelect > 1 ? "s" : ""}`;
      } else if (_minSelect === maxSelect) {
        return `Please select exactly ${_minSelect} option${_minSelect > 1 ? "s" : ""}`;
      } else {
        return `Select ${_minSelect} to ${maxSelect} option${maxSelect > 1 ? "s" : ""}`;
      }
    }
  };

  // Check if a section's requirements are fulfilled
  const isRequirementFulfilled = (section: any) => {
    const sectionOptions = section.customization_options.filter(
      (opt: any) => opt.is_active,
    );
    const selectedInSection = localFormData.customizations.filter((opt: any) =>
      sectionOptions.some((sectionOpt: any) => sectionOpt.id === opt.id),
    );

    const _minSelect = section.metadata?.min_select || 0;
    const maxSelect = section.metadata?.max_select || sectionOptions.length;
    // For mandatory selections: must meet minimum and not exceed maximum
    if (section.selection_type === "Mandatory") {
      return (
        selectedInSection.length >= _minSelect &&
        selectedInSection.length <= maxSelect
      );
    }

    // For optional selections: if nothing selected, it's fine; if something selected, must meet minimum and not exceed maximum
    if (section.selection_type === "Optional") {
      if (selectedInSection.length === 0) {
        return true; // Nothing selected is fine for optional
      } else {
        return (
          selectedInSection.length >= _minSelect &&
          selectedInSection.length <= maxSelect
        );
      }
    }

    return true; // Default case
  };

  // Get filtered weight variants (same logic as ProductForm)
  const weightVariants = React.useMemo(() => {
    if (!product?.weight_variants || product.weight_variants.length <= 1)
      return [];

    let variants = [];
    if (product.is_theme_cake && localFormData.selectedFlavor) {
      variants = product.weight_variants
        .filter(
          (variant: any) =>
            variant.flavour?.id === localFormData.selectedFlavor.id,
        )
        .sort((a: any, b: any) => a.weight - b.weight);
    } else {
      variants = [...product.weight_variants].sort(
        (a: any, b: any) => a.weight - b.weight,
      );
    }
    return variants;
  }, [product, localFormData.selectedFlavor]);

  const hasValidationErrors = Object.keys(validationErrors).length > 0;

  return (
    <div
      className="
        bg-white
        flex flex-col
        /* Mobile: Bottom Sheet */
        relative w-full max-h-[75vh] max-h-[75dvh] rounded-t-2xl animate-slideUp
        /* Tablet & Desktop: Normal Modal */
        sm:max-w-lg sm:max-h-[85vh] sm:rounded-2xl sm:shadow-2xl sm:animate-in sm:zoom-in-95 sm:duration-200
      "
    >
      {/* Header */}
      {renderHeaderContent?.() ?? (
        <div className="sticky top-0 bg-white border-b border-gray-200 p-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex-1 min-w-0 pr-2">
              <h2 className="text-lg font-semibold text-[#303030]">
                {product?.name}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 flex-shrink-0 p-1"
            >
              <X size={20} />
            </button>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-6 no-scrollbar">
        {/* Weight Selection Section (Internal design) */}
        {weightVariants.length > 1 && (
          <div className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <h3 className="font-medium text-[#303030] text-base">
                  Select Weight
                </h3>
                <span className="text-xs text-[#037A6A] bg-[#037A6A] bg-opacity-10 px-2 py-1 rounded">
                  Mandatory
                </span>
              </div>
              <p
                className={`text-xs font-normal ${
                  validationErrors.weight ? "text-red-500" : "text-[#5A5A5A]"
                }`}
              >
                {validationErrors.weight || "Please select one option"}
              </p>
            </div>

            <div className="space-y-1 border border-[#037A6A] rounded-lg p-3">
              {weightVariants.map((variant: any) => {
                const isSelected =
                  localFormData.selectedVariant?.id === variant.id;
                const priceLabel = localFormData.isEggless
                  ? variant.eggless_price_amount
                  : variant.price_amount;

                return (
                  <label
                    key={variant.id}
                    className="flex items-center space-x-3 p-2 rounded-lg cursor-pointer transition-colors"
                  >
                    <input
                      type="radio"
                      name="weight-selection"
                      checked={isSelected}
                      onChange={() => {
                        setLocalFormData((prev: any) => ({
                          ...prev,
                          selectedVariant: variant,
                        }));
                        setValidationErrors((prev) => {
                          const newErrors = { ...prev };
                          delete newErrors.weight;
                          return newErrors;
                        });
                      }}
                      className="w-4 h-4 text-[#037A6A] border-gray-300 focus:ring-[#037A6A] accent-[#037A6A]"
                    />
                    <div className="flex-1">
                      <span
                        className={`text-sm font-semibold ${
                          isSelected ? "text-[#037A6A]" : "text-[#303030]"
                        }`}
                      >
                        {variant.weight} kg
                      </span>
                      {priceLabel && (
                        <span
                          className={`text-sm ml-2 ${
                            isSelected ? "text-[#037A6A]" : "text-[#5A5A5A]"
                          }`}
                        >
                          (₹{priceLabel})
                        </span>
                      )}
                    </div>
                  </label>
                );
              })}
            </div>
          </div>
        )}

        {/* Customization Sections */}
        {product?.customization_info?.map(
          (customizationSection: any, sectionIndex: number) => {
            const activeOptions =
              customizationSection.customization_options.filter(
                (option: any) => option.is_active,
              );
            const inactiveOptions =
              customizationSection.customization_options.filter(
                (option: any) => !option.is_active,
              );
            const sectionError = validationErrors[customizationSection.label];

            return (
              <div key={sectionIndex} className="space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <h3 className="font-medium text-[#303030] text-base">
                      {customizationSection.label}
                    </h3>
                    {customizationSection.selection_type === "Optional" && (
                      <span className="text-xs text-[#5A5A5A] bg-gray-100 px-2 py-1 rounded">
                        Optional
                      </span>
                    )}
                    {customizationSection.selection_type === "Mandatory" && (
                      <span className="text-xs text-[#037A6A] bg-[#037A6A] bg-opacity-10 px-2 py-1 rounded">
                        Mandatory
                      </span>
                    )}
                  </div>
                  <p
                    className={`text-xs font-normal ${
                      sectionError ||
                      !isRequirementFulfilled(customizationSection)
                        ? "text-red-500"
                        : "text-[#5A5A5A]"
                    }`}
                  >
                    {sectionError || getSectionSubtext(customizationSection)}
                  </p>
                  {interactionErrors[customizationSection.label] && (
                    <p className="text-xs font-normal text-red-500 mt-1">
                      {interactionErrors[customizationSection.label]}
                    </p>
                  )}
                </div>

                {/* Active Options */}
                <div className="space-y-1 border border-[#037A6A] rounded-lg p-3 ">
                  {activeOptions.map((option: any) => {
                    const isSelected = localFormData.customizations.some(
                      (c: any) => c.id === option.id,
                    );
                    const priceAddition = parseFloat(
                      option.price_addition || "0",
                    );

                    return (
                      <label
                        key={option.name}
                        className={`flex items-center space-x-3 p-2 rounded-lg cursor-pointer transition-colors 
                        `}
                      >
                        <input
                          type={(() => {
                            const _minSelect =
                              customizationSection.metadata?.min_select || 0;
                            const maxSelect =
                              customizationSection.metadata?.max_select ||
                              customizationSection.customization_options.filter(
                                (opt: any) => opt.is_active,
                              ).length;
                            return maxSelect === 1 ? "radio" : "checkbox";
                          })()}
                          name={`section-${sectionIndex}-${customizationSection.label}`}
                          checked={isSelected}
                          onChange={() =>
                            handleCustomization(option, customizationSection)
                          }
                          className={`${(() => {
                            const _minSelect =
                              customizationSection.metadata?.min_select || 0;
                            const maxSelect =
                              customizationSection.metadata?.max_select ||
                              customizationSection.customization_options.filter(
                                (opt: any) => opt.is_active,
                              ).length;
                            return maxSelect === 1
                              ? "w-4 h-4 text-[#037A6A] border-gray-300 focus:ring-[#037A6A] accent-[#037A6A]"
                              : "w-4 h-4 text-[#037A6A] border-gray-300 rounded focus:ring-[#037A6A] accent-[#037A6A]";
                          })()}`}
                        />
                        <div className="flex-1">
                          <span
                            className={`text-sm font-semibold ${
                              isSelected ? "text-[#037A6A]" : "text-[#303030]"
                            }`}
                          >
                            {option.name}
                          </span>
                          {priceAddition > 0 && (
                            <span
                              className={`text-sm ml-2 ${
                                isSelected ? "text-[#037A6A]" : "text-[#5A5A5A]"
                              }`}
                            >
                              (+₹{priceAddition})
                            </span>
                          )}
                        </div>
                      </label>
                    );
                  })}
                </div>

                {/* Inactive Options */}
                {inactiveOptions.length > 0 && (
                  <div className="space-y-3">
                    <h4 className="text-xs font-medium text-[#EA5B2F] uppercase tracking-wide">
                      Unavailable at the moment
                    </h4>
                    {inactiveOptions.map((option: any) => {
                      const priceAddition = parseFloat(
                        option.price_addition || "0",
                      );

                      return (
                        <div
                          key={option.name}
                          className="flex items-center space-x-3 p-3 rounded-lg bg-gray-50 border border-gray-200"
                        >
                          <input
                            type={(() => {
                              const _minSelect =
                                customizationSection.metadata?.min_select || 0;
                              const maxSelect =
                                customizationSection.metadata?.max_select ||
                                customizationSection.customization_options.filter(
                                  (opt: any) => opt.is_active,
                                ).length;
                              return maxSelect === 1 ? "radio" : "checkbox";
                            })()}
                            name={`section-${sectionIndex}-${customizationSection.label}`}
                            disabled
                            className={`${(() => {
                              const _minSelect =
                                customizationSection.metadata?.min_select || 0;
                              const maxSelect =
                                customizationSection.metadata?.max_select ||
                                customizationSection.customization_options.filter(
                                  (opt: any) => opt.is_active,
                                ).length;
                              return maxSelect === 1
                                ? "w-4 h-4 text-[#037A6A] border-gray-300"
                                : "w-4 h-4 text-[#037A6A] border-gray-300 rounded";
                            })()}`}
                          />
                          <div className="flex-1">
                            <span className="text-sm font-medium text-gray-400">
                              {option.name}
                            </span>
                            {priceAddition > 0 && (
                              <span className="text-sm ml-2 text-gray-400">
                                (+₹{priceAddition})
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          },
        )}
      </div>

      {/* Footer */}
      <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4">
        <div className="flex items-center justify-between">
          {/* Quantity Selector */}
          <div className="flex items-center space-x-3">
            <button
              onClick={() => quantity > 1 && handleQuantityChange(quantity - 1)}
              className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center text-gray-600 hover:bg-gray-100"
              disabled={quantity <= 1}
            >
              -
            </button>
            <span className="text-lg font-medium text-[#303030] min-w-[2rem] text-center">
              {quantity}
            </span>
            <button
              onClick={() =>
                quantity < 10 && handleQuantityChange(quantity + 1)
              }
              className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center text-gray-600 hover:bg-gray-100"
              disabled={quantity >= 10}
            >
              +
            </button>
          </div>
          {/* Add to Cart Button */}
          <button
            onClick={handleConfirm}
            disabled={hasValidationErrors}
            className={`px-6 py-3 rounded-lg font-medium transition-colors flex items-center space-x-2 ${
              hasValidationErrors
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "bg-[#037A6A] text-white hover:bg-[#049e8b]"
            }`}
          >
            <span>{isBuyNow ? "Buy Now" : "Add Item"}</span>
            <span>| ₹{parseFloat(currentPrice.toFixed(2).toString())}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomizationModal;
